import type { ApiError } from '@oip/contracts';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** List query params where `sort` is widened to string (grids emit column keys). */
export type ListParams<T> = Partial<Omit<T, 'sort'>> & { sort?: string };

/** Session holder — AuthProvider keeps this current; the client reads it per request. */
export const session: {
  token: string | null;
  tenantId: string | null;
  refreshToken: string | null;
  /**
   * The PARTNER a platform administrator has entered, if any.
   *
   * ⚠️ Set ONLY by `assumePartner`, and honoured by the server only for a caller whose
   * `isPlatformAdmin` it re-reads from the database on that request — so this is a
   * statement of where the reader is standing, never authority. Anybody else sending it
   * is ignored, and their session decides (D15).
   */
  partnerId: string | null;
  resellerId: string | null;
} = {
  token: null,
  tenantId: null,
  refreshToken: null,
  partnerId: null,
  resellerId: null,
};

/**
 * Platform-scoped API prefix. Requests under it are tenant-independent by
 * construction (PlatformAdminGuard runs without TenantContextGuard), so the
 * tenant header must never be attached.
 *
 * This is decided centrally rather than per call site: a platform page that
 * forgot an option would silently send a tenant header, which is exactly the
 * kind of scope leak the platform/tenant split exists to prevent.
 */
export const PLATFORM_PREFIX = '/api/v1/platform';

/** Paths that are authenticated but carry no tenant context. */
function sendsTenantHeader(path: string): boolean {
  if (path.startsWith(PLATFORM_PREFIX)) return false;
  if (path.startsWith('/api/v1/auth/')) return false;
  return true;
}

/**
 * Central handler for an authenticated 401.
 *
 * Registered once by AuthProvider. Kept as a module-level hook rather than
 * threaded through every call so there is exactly one place that decides a
 * session has ended — page-level handling would inevitably be inconsistent.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/** Error carrying the API's standard envelope so the UI can branch on `code`. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly details?: ApiError['details'];
  constructor(status: number, body: Partial<ApiError>) {
    super(body.message ?? `Request failed (${status})`);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body.code ?? 'ERROR';
    this.correlationId = body.correlationId;
    this.details = body.details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Skip tenant header (used by /auth endpoints before a tenant is chosen). */
  noTenant?: boolean;
  /**
   * Read ANOTHER tenant, without switching the session into it.
   *
   * For a reader that spans tenants the caller can already reach — a white-label
   * partner expanding a reseller in its hierarchy tree, which would otherwise
   * have to switch tenant, load, and switch back to render one row. The server
   * decides whether they may: this is the same header the switcher sets, so it
   * grants nothing that entering the tenant would not.
   *
   * Deliberately not a way to WRITE somewhere else. Nothing passes it on a
   * mutation, and a mutation that did would be doing something the person never
   * saw themselves navigate to.
   */
  asTenant?: string;
  /**
   * Treat a 401 as an ordinary request failure rather than session expiry.
   * Used by change-password, where 401 means "current password is wrong".
   */
  skipSessionExpiry?: boolean;
  /** Internal: set on the single retry after a successful token renewal. */
  _retried?: boolean;
}

function buildQuery(query?: Record<string, unknown>): string {
  if (!query) return '';
  const p = new URLSearchParams();
  const put = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return;
    p.append(k, v instanceof Date ? v.toISOString() : String(v));
  };
  for (const [k, v] of Object.entries(query)) {
    /*
     * ⚠️ **AN ARRAY BECOMES REPEATED PARAMETERS, and it could not before.**
     * `String(['A','B'])` is `"A,B"`, so an array was sent as ONE parameter
     * containing a comma — which the server reads as a single value literally
     * named `A,B`, matching nothing. So the client could never send a repeated
     * query parameter for ANY endpoint, although `logSearchQuerySchema.tier`
     * and `.service` have accepted arrays all along.
     *
     * Pre-existing and latent: every caller happened to send one value. Found by
     * CLICKING a dashboard area panel — the link carried eighteen codes, the API
     * answered three lines when asked by hand, and the page showed zero.
     */
    if (Array.isArray(v)) for (const item of v) put(k, item);
    else put(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * The refresh in flight, if any.
 *
 * Shared so a burst of requests that all 401 at once produces ONE exchange
 * rather than one each. That matters more than it sounds: refresh tokens are
 * single-use, so five concurrent exchanges would spend the token five times and
 * the server would read four of those as reuse — revoking the family and
 * signing the user out for behaving normally.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Called with a fresh token pair so the app can persist it. */
let onTokensRenewed: ((tokens: { accessToken: string; refreshToken: string }) => void) | null =
  null;

export function setOnTokensRenewed(
  fn: ((tokens: { accessToken: string; refreshToken: string }) => void) | null,
): void {
  onTokensRenewed = fn;
}

async function renewSession(): Promise<boolean> {
  if (session.refreshToken === null) return false;
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!res.ok) return false;
      const pair = (await res.json()) as { accessToken: string; refreshToken: string };
      session.token = pair.accessToken;
      session.refreshToken = pair.refreshToken;
      onTokensRenewed?.(pair);
      return true;
    } catch {
      // Offline, or the API is down. Not a session failure — the caller retries
      // nothing and surfaces the original error.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (session.token) headers.authorization = `Bearer ${session.token}`;
  // Path decides scope; `noTenant` remains available as an explicit override.
  if (opts.asTenant && sendsTenantHeader(path)) {
    headers['x-tenant-id'] = opts.asTenant;
  } else if (session.tenantId && !opts.noTenant && sendsTenantHeader(path)) {
    headers['x-tenant-id'] = session.tenantId;
  }
  /*
   * ⚠️ Sent on every request while a partner is assumed, EXCEPT platform-scoped paths.
   *
   * `sendsTenantHeader` already draws that line for the tenant header — a platform route
   * runs without `TenantContextGuard` by construction — and the partner console's routes
   * are tenant-prefixed (`/admin/partner/...`), so the same predicate is the right one.
   * Sending it under `/platform` would attach a context to routes designed to have none.
   */
  if (session.partnerId && sendsTenantHeader(path)) {
    headers['x-partner-id'] = session.partnerId;
    if (session.resellerId) headers['x-reseller-id'] = session.resellerId;
  }

  const res = await fetch(`${BASE}${path}${buildQuery(opts.query)}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const error = new ApiClientError(res.status, (parsed ?? {}) as Partial<ApiError>);
    // A 401 on an AUTHENTICATED request means the session is gone: expired,
    // revoked, deactivated, or invalidated by a password change. A 401 on a
    // request that carried no token is an ordinary credential failure (login,
    // or a wrong current password) and must not end a session that does not
    // exist yet. `skipSessionExpiry` covers the authenticated-but-not-session
    // case — chiefly change-password, where 401 means "wrong password".
    if (res.status === 401 && session.token && !opts.skipSessionExpiry) {
      /*
       * Try to renew before declaring the session over.
       *
       * An hour-old access token is the ordinary case, not an incident — and
       * ending the session for it is what made this application unusable on a
       * phone. `_retried` bounds it to one attempt: if the request 401s again
       * with a token minted seconds ago, the problem is authorisation rather
       * than expiry and retrying would loop.
       */
      if (!opts._retried && (await renewSession())) {
        return request<T>(path, { ...opts, _retried: true });
      }
      onUnauthorized?.();
    }
    throw error;
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  /** A read scoped to a tenant other than the active one. See `asTenant`. */
  getAs: <T>(path: string, asTenant: string, query?: Record<string, unknown>) =>
    request<T>(path, { query, asTenant }),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  /**
   * A POST that READS, scoped to a tenant other than the active one.
   *
   * ⚠️ **Named for what it is allowed to do, because `postAs` would be a
   * loaded gun.** `getAs` exists so the dashboard and the partner console can
   * read a branch without switching into it, and the rule beside it has always
   * been that nothing passes it on a MUTATION — acquiring write context from a
   * header is a second authorization path, which is the thing this programme
   * keeps refusing to build.
   *
   * `POST /query` is a read that computes rather than a write (an integration
   * case compares table counts across repeated runs), so it needs the same
   * facility and none of the licence. A call site that wants to change
   * something and reaches for this has to rename it first.
   */
  postReadOnlyAs: <T>(path: string, asTenant: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, asTenant }),
  patch: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  /**
   * A full, idempotent replacement.
   *
   * ⚠️ **Not `patch`, and the difference is a claim about semantics rather than
   * taste.** PATCH says "merge these fields into what is there"; the log-level
   * override is keyed by service and REPLACES the row wholesale, so the same
   * request sent twice must leave exactly the state one would. Spelling it PATCH
   * would invite a later caller to send half an override and expect the rest to
   * survive.
   */
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  // Named `del` because `delete` is a reserved word. `request` already returns
  // undefined for 204, which is what these endpoints answer.
  del: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

/**
 * PUT bytes straight to a presigned object-store URL.
 *
 * Deliberately NOT routed through `api`/`request`: that helper attaches the
 * bearer token and tenant header and forces a JSON content type. Sending an
 * OIP token to a third-party storage host would leak the session, and the
 * presigned URL carries its own authorisation in the query string.
 */
export async function putToPresignedUrl(
  url: string,
  file: Blob,
  headers: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    // Sent verbatim as the API supplied them. They may carry the expected
    // SHA-256, which the store validates on arrival — the browser does not need
    // to know which of them matter.
    headers,
    body: file,
    signal,
  });
  if (!res.ok) {
    throw new Error(`Upload failed: the storage service answered ${res.status}.`);
  }
}

/**
 * Download a file the API generates, with the session attached.
 *
 * ⚠️ **NOT `window.open(url)`, and not for convenience.** Every route on this
 * API needs a bearer token, and a navigation carries no `Authorization` header —
 * so opening the URL directly answers 401 and the reader sees the JSON error in
 * a blank tab, which reads as the product being broken. It also cannot be fixed
 * by putting the token in the query string: that would put a credential in the
 * browser history, the server's access log and any proxy in between.
 *
 * ⚠️ **NOT routed through `request`**, for the reason `putToPresignedUrl` is not:
 * that helper parses the answer as JSON, and a CSV is not JSON. It shares the
 * session and the refresh logic deliberately, because a download is the one
 * action most likely to be the first thing a reader does after leaving a tab
 * open — so a 401 here has to end the session exactly as it does everywhere else
 * rather than silently producing an empty file.
 *
 * ⚠️ **The FILENAME comes from `Content-Disposition`**, which a blob URL does not
 * honour by itself — so it is parsed and set on the anchor. The server computed
 * it (for the log export it carries the window the file covers), and falling back
 * to the browser's default would hand somebody `export.csv` on the one download
 * whose whole job is identifying what is in it.
 */
export async function downloadWithSession(
  path: string,
  query?: Record<string, unknown>,
  fallbackFilename = 'download',
): Promise<void> {
  const headers: Record<string, string> = {};
  if (session.token) headers.authorization = `Bearer ${session.token}`;
  const url = `${BASE}${path}${buildQuery(query)}`;

  let res = await fetch(url, { headers });
  if (res.status === 401 && session.token) {
    // One retry after a renewal, exactly as `request` does — and the same shared
    // refresh, so a download racing a page load cannot spend the token twice.
    if (await renewSession()) {
      const retryHeaders: Record<string, string> = {};
      if (session.token) retryHeaders.authorization = `Bearer ${session.token}`;
      res = await fetch(url, { headers: retryHeaders });
    } else {
      onUnauthorized?.();
      throw new Error('Your session has ended. Sign in again to download this.');
    }
  }

  if (!res.ok) {
    /*
     * ⚠️ The SERVER's own message, because the refusals here are the actionable
     * kind: "more than 10,000 lines match, narrow the range" and "this
     * deployment has no log store". A generic "download failed" would hide the
     * only sentence that says what to do next.
     */
    let message = `The download failed (${res.status}).`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      // Not JSON. The status is all there is.
    }
    throw new Error(message);
  }

  const filename =
    filenameFromDisposition(res.headers.get('content-disposition')) ?? fallbackFilename;
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

/**
 * ⚠️ Deliberately narrow: `filename="…"` and nothing else. RFC 6266 also allows
 * `filename*=UTF-8''…`, which nothing here emits — and a parser for a form we do
 * not produce is a parser nothing tests. An unrecognised header returns null so
 * the caller's fallback applies, rather than a half-parsed name.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = /filename="([^"]+)"/.exec(header);
  return m?.[1] ?? null;
}
